<template>
    <VueFinalModal
        v-bind="options"
        class="flex items-center justify-center"
        :content-class="contentClass"
        :content-style="contentStyle"
        :model-value="props.modelValue"
        :click-to-close="!preventBackdropClick"
        @update:model-value="handleModelValueUpdate"
    >
        <div class="p-5" tabindex="-1">
            <button
                v-if="!hideCloser"
                aria-label="Close modal"
                class="modal-close absolute right-4 top-4 text-black dark:text-white hover:text-gray-600 dark:hover:text-txt-body z-10 text-4xl"
                @click="requestClose()"
            >
                ×
            </button>

            <div v-if="title" class="modal-header mb-4">
                <h2 id="modal-title" class="text-xl font-semibold text-gray-900 dark:text-white">{{ title }}</h2>
            </div>

            <div class="modal-body">
                <slot />
            </div>

            <div v-if="$slots.footer" class="modal-footer mt-4">
                <slot name="footer" />
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { VueFinalModal } from 'vue-final-modal'

/**
 * Base modal wrapper component using vue-final-modal.
 *
 * This is a convenience wrapper for template-driven modals with v-model binding.
 * For programmatic modals (promise-based, nested), use `useModal` hook directly.
 *
 * @example
 * <CModal v-model="isOpen" title="My Modal" :preventBackdropClick="true">
 *   <p>Modal content here</p>
 *   <template #footer>
 *     <button @click="isOpen = false">Close</button>
 *   </template>
 * </CModal>
 */

// Default options for all modals
const options = {
    teleportTo: 'body' as const,
    displayDirective: 'if' as const,
    hideOverlay: false,
    overlayTransition: 'vfm-fade',
    contentTransition: 'vfm-fade',
    clickToClose: true,
    escToClose: true,
    background: 'non-interactive' as const,
    lockScroll: true,
    reserveScrollBarGap: true,
    transitionDuration: 100
}

const attrs = useAttrs()

const props = withDefaults(defineProps<{
    modelValue?: boolean
    hideCloser?: boolean
    title?: string
    preventBackdropClick?: boolean
    size?: 'sm' | 'md' | 'lg' | 'xl'
    width?: string | number
    stretch?: boolean
}>(), {
    modelValue: true,
    hideCloser: false,
    title: undefined,
    preventBackdropClick: false,
    size: 'md',
    width: undefined,
    stretch: false
})

const sizeClass = computed(() => {
    switch (props.size) {
        case 'sm': return 'max-w-sm'
        case 'lg': return 'max-w-3xl'
        case 'xl': return 'max-w-4xl'
        case 'md':
        default: return 'max-w-2xl'
    }
})

const contentClass = computed(() => {
    const widthClass = props.width ? '' : sizeClass.value
    const stretchClass = props.stretch ? 'w-full' : ''

    return `relative flex flex-col mx-4 p-4 bg-white dark:bg-surface-card rounded-lg overflow-visible ${widthClass} ${stretchClass}`.trim()
})

const contentStyle = computed(() => {
    if (props.width === undefined) {
        return undefined
    }

    return {
        width: typeof props.width === 'number' ? `${props.width}px` : props.width
    }
})

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    close: []
    'before-close': [close: () => void]
}>()

function finalizeClose() {
    emit('update:modelValue', false)
    emit('close')
}

function requestClose() {
    if (attrs.onBeforeClose) {
        emit('before-close', finalizeClose)
        return
    }

    finalizeClose()
}

function handleModelValueUpdate(value: boolean) {
    if (value) {
        emit('update:modelValue', true)
        return
    }

    requestClose()
}
</script>
